import de from '../lang/locales/de.json';
import en from '../lang/locales/en.json';
import ja from '../lang/locales/ja.json';
import zh_cn from '../lang/locales/zh_cn.json';
import zh_tw from '../lang/locales/zh_tw.json';
import IntlMessageFormat from 'intl-messageformat';
import { getEnvironment } from './environment';

interface MessageCatalog {
    [key: string]: string;
}

const LOCALE_CODES = ['en', 'de', 'ja', 'zh-cn', 'zh-tw'] as const;
const DM_LANG_CODES = ['ENGLISH', 'GERMAN', 'JAPANESE', 'CHINESE', 'CHINESE_TRADITIONAL'] as const;
type LocaleCode = (typeof LOCALE_CODES)[number];
type DmLangCode = (typeof DM_LANG_CODES)[number];

/**
 * Locale interface
 *
 * @param locale Short locale code (e.g. 'en', 'zh-tw').
 * @param name Language name (e.g. 'English', '繁體中文').
 * @param messages Corresponding message catalog.
 * @param pimcoreCode Corresponding Pimcore locale code.
 * @param dmLangCode Corresponding DefinitionManager language code.
 * @param enabled Whether the locale is enabled for users. This effects more than just the UI
                  language selector. For example, if a locale is disabled, RL items won't be
                  display buttons for assets in that locale.
 */
interface Locale {
    locale: LocaleCode;
    name: string;
    messages: MessageCatalog;
    pimcoreCode: string;
    dmLangCode: DmLangCode;
    enabled: boolean;
}

const LOCALE_BY_CODE: Record<LocaleCode, Locale> = {
    en: {
        locale: 'en',
        name: 'English',
        messages: en,
        pimcoreCode: 'en',
        dmLangCode: 'ENGLISH',
        enabled: true,
    },
    de: {
        locale: 'de',
        name: 'Deutsch',
        messages: de,
        pimcoreCode: 'de',
        dmLangCode: 'GERMAN',
        enabled: true,
    },
    ja: {
        locale: 'ja',
        name: '日本語',
        messages: ja,
        pimcoreCode: 'ja',
        dmLangCode: 'JAPANESE',
        enabled: true,
    },
    'zh-cn': {
        locale: 'zh-cn',
        name: '简体中文',
        messages: zh_cn,
        pimcoreCode: 'zh',
        dmLangCode: 'CHINESE',
        enabled: true,
    },
    'zh-tw': {
        locale: 'zh-tw',
        name: '繁體中文',
        messages: zh_tw,
        pimcoreCode: 'zh_Hant_TW',
        dmLangCode: 'CHINESE_TRADITIONAL',
        enabled: true,
    },
};

const LOCALES: [Locale, ...Locale[]] = Object.values(LOCALE_BY_CODE) as [Locale, ...Locale[]];
const ENABLED_LOCALES = LOCALES.filter((locale) => locale.enabled);
const LOCALE_CODE_SET = new Set<string>(LOCALE_CODES);

/**
 * Get the locale object for the given fuzzy locale string.
 * If the exact locale is not found, it will try to find the language code.
 * If the language code is not found, it will default to the first language in the list.
 *
 * @param locale - A locale string (e.g. 'en', 'zh-tw') or an array of locale strings in preference order.
 */
function findLocale(input: string | readonly string[]): Locale {
    const candidates = Array.isArray(input) ? input : [input];

    for (const locale of candidates) {
        const lower = locale.toLowerCase();

        const exactMatch = LOCALES.find((l) => l.enabled && l.locale === lower);
        if (exactMatch) {
            return exactMatch;
        }

        const languageMatch = LOCALES.find((l) => l.enabled && l.locale.split('-')[0] === lower);
        if (languageMatch) {
            return languageMatch;
        }
    }

    return LOCALES[0];
}

// Cache for IntlMessageFormat instances: key is `${template}|${localeCode}`
const messageFormatCache = new Map<string, IntlMessageFormat>();

/**
 * Format an ICU string with the given values and locale, using a cache
 * of previously created IntlMessageFormat instances.
 */
function formatIcuString(
    template: string,
    values: Record<string, any>,
    locale: LocaleCode | Locale
): string {
    const localeCode = typeof locale === 'string' ? locale : locale.locale;
    const cacheKey = `${localeCode}::${template}`;

    let msgFormatter = messageFormatCache.get(cacheKey);
    if (!msgFormatter) {
        try {
            msgFormatter = new IntlMessageFormat(template, localeCode);
            messageFormatCache.set(cacheKey, msgFormatter);
        } catch (err) {
            // If creation fails, log and fallback immediately
            console.error('[i18n] formatIcuString failed:', {
                template,
                values,
                locale: localeCode,
                error: err,
            });
            return template.replace(/\{([^,}]+)(?:,[^}]+)?\}/g, (_, key) => {
                const v = values[key];
                return v != null ? String(v) : '';
            });
        }
    }

    try {
        return msgFormatter.format(values) as string;
    } catch (err) {
        // Log formatting errors and fallback
        console.error('[i18n] formatIcuString failed:', {
            template,
            values,
            locale: localeCode,
            error: err,
        });
        return template.replace(/\{([^,}]+)(?:,[^}]+)?\}/g, (_, key) => {
            const v = values[key];
            return v != null ? String(v) : '';
        });
    }
}

export const getLocalizedText = (() => {
    return (language: string, translations: Record<string, string>, fallback = 'en'): string => {
        const lowerLang = language.toLowerCase();
        return translations[lowerLang] || translations[fallback] || '';
    };
})();

function getValidLocaleCode(value?: string | null): LocaleCode | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalized = value.toLowerCase();
    return LOCALE_CODE_SET.has(normalized) ? (normalized as LocaleCode) : undefined;
}

export {
    LOCALES,
    ENABLED_LOCALES,
    findLocale,
    formatIcuString,
    LOCALE_CODES,
    LOCALE_BY_CODE,
    getValidLocaleCode,
};
export type { LocaleCode, DmLangCode, Locale, MessageCatalog };
